import * as React from 'react';

import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
    getPaginationRowModel,
} from '@tanstack/react-table';
import {useEffect} from "react";
import {tab} from "@testing-library/user-event/dist/tab";

export const TanStackTable = ({columns, tableData, forceNewData = false, laravelPaginationMetaData = null, page = 0, onChangePage = null, onSortingChange = null, sortBy = null, sortOrder = null})=> {
    const [data, setData] = React.useState(() => []);
    const [sorting, setSorting] = React.useState(() => {
        if (sortBy && sortOrder) {
            return [{id: sortBy, desc: sortOrder === 'desc'}];
        }
        return [];
    });

    const [loading, setLoading] = React.useState(true);

    const handleSortingChange = (updaterOrValue) => {
        const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
        setSorting(newSorting);

        if (onSortingChange && newSorting.length > 0) {
            const sortConfig = newSorting[0];
            onSortingChange(sortConfig.id, sortConfig.desc ? 'desc' : 'asc');
        } else if (onSortingChange && newSorting.length === 0) {
            onSortingChange('start_time', 'asc');
        }
    };

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        initialState: {
            pagination: {
                pageSize: laravelPaginationMetaData?.hasOwnProperty('per_page') ? laravelPaginationMetaData.per_page : 15,
                pageIndex: laravelPaginationMetaData?.hasOwnProperty('current_page') ? laravelPaginationMetaData.current_page - 1 : page,
            },
        },
        manualPagination: laravelPaginationMetaData !== null,
        manualSorting: onSortingChange !== null,
        autoResetPageIndex: false,
        rowCount: laravelPaginationMetaData?.hasOwnProperty('total') ? laravelPaginationMetaData.total : tableData.length,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: handleSortingChange,
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    useEffect(() => {
        (async () => {
            await setLoading(true);
            await setData([...tableData]);
            await setLoading(false);
        })()
    }, [page, tableData]);

    useEffect(() => {
        if (laravelPaginationMetaData?.current_page !== undefined) {
            const serverPageIndex = laravelPaginationMetaData.current_page - 1;
            if (serverPageIndex !== table.getState().pagination.pageIndex) {
                table.setPageIndex(serverPageIndex);
            }
        }
    }, [laravelPaginationMetaData?.current_page]);

    useEffect(() => {
        if (sortBy && sortOrder) {
            const newSorting = [{id: sortBy, desc: sortOrder === 'desc'}];
            if (sorting.length === 0 || sorting[0].id !== sortBy || sorting[0].desc !== (sortOrder === 'desc')) {
                setSorting(newSorting);
            }
        } else if (sorting.length > 0) {
            setSorting([]);
        }
    }, [sortBy, sortOrder]);


    const goToNextPage = () => {
        if(canGoToNextPage()) {
            if(laravelPaginationMetaData !== null) {
                onChangePage(table.getState().pagination.pageIndex + 1);
                table.setPageIndex(Number(table.getState().pagination.pageIndex + 1));
            } else {
                table.nextPage();
            }
        }
    }

    const goToPreviousPage = () => {
        if(canGoToPrevious()) {
            table.previousPage();

            if(laravelPaginationMetaData !== null) {
                onChangePage(table.getState().pagination.pageIndex - 1);
            }
        }
    }

    const getPageCountFromMetaData = () => {
        if(laravelPaginationMetaData !== null) {
            return laravelPaginationMetaData?.last_page;
        }

        return table.getPageCount();
    }

    const canGoToNextPage = () => {
        if(laravelPaginationMetaData !== null) {
            return laravelPaginationMetaData?.last_page > table.getState().pagination.pageIndex + 1;
        }

        return table.getCanNextPage();
    };

    const canGoToPrevious = () => {
        if (laravelPaginationMetaData !== null) {
            return table.getState().pagination.pageIndex > 0;
        }

        return table.getCanPreviousPage();
    };

    return (
        <>
            <div className="flex flex-col mx-auto">
                <table>
                    <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                            key={headerGroup.id}
                        >
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                                >
                                    {header.isPlaceholder ? null : (
                                        <div
                                            {...{
                                                className: header.column.getCanSort()
                                                    ? 'cursor-pointer select-none flex min-w-[36px]'
                                                    : '',
                                                onClick: header.column.getToggleSortingHandler(),
                                            }}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {{
                                                asc: <span className="pl-2">↑</span>,
                                                desc: <span className="pl-2">↓</span>,
                                            }[header.column.getIsSorted()] ?? null}
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {
                        loading && (
                            <tr>
                                <td colSpan={table.getVisibleLeafColumns().length} className="py-8">
                                    <div className="flex justify-center items-center">
                                        <div
                                            className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
                                    </div>
                                </td>
                            </tr>
                        )
                    }

                    {!loading && table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="border-b">
                        {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="px-5 pt-[14px] pb-[18px]">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>

                <div className="flex sm:flex-row flex-col w-full mt-8 items-end justify-end gap-2 text-xs">
                    <div className="flex gap-2">
                        <button
                            className={`${
                                !canGoToPrevious()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => {
                                if(laravelPaginationMetaData !== null) {
                                    onChangePage(0);
                                    table.setPageIndex(0);
                                } else {
                                    table.setPageIndex(0);
                                }
                            }}
                            disabled={!canGoToPrevious()}
                        >
                            <span className="w-5 h-5">{'<<'}</span>
                        </button>
                        <button
                            className={`${
                                !canGoToPrevious()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => {
                                goToPreviousPage()
                            }}
                            disabled={!canGoToPrevious()}
                        >
                            <span className="w-5 h-5">{'<'}</span>
                        </button>
                        <span className="flex items-center gap-1">
      <input
          min={1}
          max={getPageCountFromMetaData()}
          type="number"
          value={table.getState().pagination.pageIndex + 1}
          onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) : 1;
              const maxPage = getPageCountFromMetaData();

              if (page < 1 || page > maxPage) {
                  return;
              }

              const pageIndex = page - 1;

              if(laravelPaginationMetaData !== null) {
                  onChangePage(pageIndex);
                  table.setPageIndex(pageIndex);
              } else {
                  table.setPageIndex(pageIndex);
              }
          }}
          className="border p-1 rounded w-10"
      />
      of {getPageCountFromMetaData()}
    </span>
                        <button
                            className={`${
                                !canGoToNextPage()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => {
                                goToNextPage()
                            }}
                            disabled={!canGoToNextPage()}
                        >
                            <span className="w-5 h-5">{'>'}</span>
                        </button>
                        <button
                            className={`${
                                !canGoToNextPage()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => {
                                const lastPageIndex = getPageCountFromMetaData() - 1;
                                if(laravelPaginationMetaData !== null) {
                                    onChangePage(lastPageIndex);
                                    table.setPageIndex(lastPageIndex);
                                } else {
                                    table.setPageIndex(lastPageIndex);
                                }
                            }}
                            disabled={!canGoToNextPage()}
                        >
                            <span className="w-5 h-5">{'>>'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </>

    );
}